"use client"

import React, { useState, useEffect, useRef, useCallback } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Textarea } from "@/components/ui/textarea"
import { Badge } from "@/components/ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { ScrollArea } from "@/components/ui/scroll-area"
import { useLanguage } from "@/contexts/LanguageContext"
import MarkdownRenderer from "@/components/MarkdownRenderer"
import Navbar from "@/components/Navbar"
import Footer from "@/components/Footer"
import { agentService, AgentSession } from "@/services/api"
import { 
  Send, 
  Bot, 
  User, 
  Brain, 
  Database, 
  Wrench, 
  ArrowRight
} from "lucide-react"

interface Message {
  id: string
  type: 'user' | 'ai'
  content: string
  timestamp: Date
  sessionId?: string
  metadata?: {
    modelId?: string
    tokens?: number
    processingTime?: number
  }
}

interface AgentStep {
  id: string
  type: 'intent' | 'data' | 'tools'
  title: string
  description: string
  content: string
  status: 'pending' | 'processing' | 'completed' | 'error'
}

export default function AgentPage() {
  const [messages, setMessages] = useState<Message[]>([])
  const [inputValue, setInputValue] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [agentSteps, setAgentSteps] = useState<AgentStep[]>([])
  const [activeTab, setActiveTab] = useState("chat")
  const [selectedModel, setSelectedModel] = useState<any>(null)
  const [textareaHeight, setTextareaHeight] = useState(80) // 初始高度
  const heightAdjustTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isCheckingAuth, setIsCheckingAuth] = useState(true)
  const [currentSession, setCurrentSession] = useState<AgentSession | null>(null)
  const [isInitializing, setIsInitializing] = useState(true)
  const [sessionError, setSessionError] = useState<string | null>(null)
  const [agentId, setAgentId] = useState<string | null>(null) // 添加agentId状态
  
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { t } = useLanguage()
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const scrollAreaRef = useRef<HTMLDivElement>(null)

  // 格式化时间显示
  const formatMessageTime = (timestamp: Date) => {
    const now = new Date()
    const diff = now.getTime() - timestamp.getTime()
    const diffMinutes = Math.floor(diff / (1000 * 60))
    const diffHours = Math.floor(diff / (1000 * 60 * 60))
    const diffDays = Math.floor(diff / (1000 * 60 * 60 * 24))

    if (diffMinutes < 1) {
      return '刚刚'
    } else if (diffMinutes < 60) {
      return `${diffMinutes}分钟前`
    } else if (diffHours < 24) {
      return `${diffHours}小时前`
    } else if (diffDays < 7) {
      return `${diffDays}天前`
    } else {
      return timestamp.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }

  // 初始化Agent步骤
  useEffect(() => {
    setAgentSteps([
      {
        id: '1',
        type: 'intent',
        title: t('agent.intentRecognition'),
        description: t('agent.intentDescription'),
        content: '',
        status: 'pending'
      },
      {
        id: '2',
        type: 'data',
        title: t('agent.dataIntegration'),
        description: t('agent.dataDescription'),
        content: '',
        status: 'pending'
      },
      {
        id: '3',
        type: 'tools',
        title: t('agent.executionTools'),
        description: t('agent.toolsDescription'),
        content: '',
        status: 'pending'
      }
    ])
  }, [t])

  // 检查用户登录状态
  useEffect(() => {
    const token = localStorage.getItem("token")
    if (token) {
      setIsAuthenticated(true)
    } else {
      // 如果没有token，重定向到登录页
      navigate("/login", { replace: true })
    }
    setIsCheckingAuth(false)
  }, [navigate])

    // 初始化会话
  useEffect(() => {
    if (!isAuthenticated || isCheckingAuth) return

    const initializeSession = async (retryCount = 0) => {
      try {
        setIsInitializing(true)
        console.log('开始初始化会话...')
        
        // 获取模型列表并设置默认选择
        console.log('获取模型列表...')
        const models = await agentService.getModels()
        console.log('模型列表:', models)
        if (models.length > 0) {
          setSelectedModel(models[0])
          console.log('设置默认模型:', models[0])
        } else {
          console.warn('没有可用的模型')
        }
        
        // 检查URL参数中是否有sessionId
        const sessionId = searchParams.get('sessionId')
        
        let session: AgentSession
        
        if (sessionId) {
          // 如果URL中有sessionId，直接使用这个session
          console.log('使用URL中的sessionId:', sessionId)
          session = { sessionId }
          setCurrentSession(session)
          // 保存sessionId到localStorage
          localStorage.setItem('agent_session_id', sessionId)
        } else {
          // 否则获取或创建当前会话
          console.log('调用 getCurrentSession...')
          session = await agentService.getCurrentSession()
          console.log('getCurrentSession 返回:', session)
          setCurrentSession(session)
        }
        
        // 获取会话历史
        console.log('调用 getSessionHistory...')
        const history = await agentService.getSessionHistory(session.sessionId)
        console.log('getSessionHistory 返回:', history)
        console.log('history.messages:', history.messages)
        console.log('history.agentId:', history.agentId)
        
        // 设置agentId
        setAgentId(history.agentId || null)
        
        // 转换历史消息格式
        const convertedMessages: Message[] = (history.messages || [])
          .filter(msg => msg.content !== null) // 过滤掉content为null的消息
          .map((msg) => ({
            id: msg.messageId || `history-${Date.now()}-${Math.random()}`,
            type: msg.role === 'user' ? 'user' : 'ai',
            content: msg.content!, // 使用!断言，因为已经过滤了null
            timestamp: new Date(parseInt(msg.gmtCreated)),
            sessionId: session.sessionId,
            metadata: {
              modelId: selectedModel?.id
            }
          }))
        
        console.log('转换后的消息:', convertedMessages)
        setMessages(convertedMessages)
        
        // 如果有历史消息，设置最后使用的模型
        if (convertedMessages.length > 0) {
          const lastMessage = convertedMessages[convertedMessages.length - 1]
          if (lastMessage.metadata?.modelId) {
            // 这里可以设置选中的模型
            // 暂时保持当前逻辑，后续可以优化
          }
        }
        
      } catch (error) {
        console.error('初始化会话失败:', error)
        console.error('错误详情:', {
          name: error instanceof Error ? error.name : 'Unknown',
          message: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined
        })
        
        // 如果是网络错误且重试次数少于3次，则重试
        if (retryCount < 3 && (error instanceof TypeError || (error instanceof Error && error.message.includes('fetch')))) {
          console.log(`会话初始化失败，${retryCount + 1}/3 次重试...`)
          setTimeout(() => initializeSession(retryCount + 1), 2000)
          return
        }
        
        // 会话初始化失败，显示错误
        setSessionError('会话初始化失败，请检查网络连接后重试')
      } finally {
        setIsInitializing(false)
      }
    }

    initializeSession()
  }, [isAuthenticated, isCheckingAuth, searchParams])

  // 从URL参数获取用户输入或sessionId
  useEffect(() => {
    if (!isAuthenticated || !currentSession || isInitializing) return
    
    const userInput = searchParams.get('input')
    const sessionId = searchParams.get('sessionId')
    
    // 如果URL中有input参数且没有历史消息，需要自动发送消息
    if (userInput && messages.length === 0) {
      console.log('检测到用户输入，准备自动发送:', userInput)
      setInputValue(userInput)
      // 延迟一下确保session完全初始化
      setTimeout(() => {
        handleSendMessage(userInput)
      }, 100)
    }
  }, [searchParams, isAuthenticated, currentSession, isInitializing, messages.length])

  // 滚动到消息历史底部
  const scrollToBottom = useCallback(() => {
    if (scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]')
      if (scrollContainer) {
        scrollContainer.scrollTo({
          top: scrollContainer.scrollHeight,
          behavior: 'smooth'
        })
      }
    }
  }, [])

  // 自动滚动到消息历史底部
  useEffect(() => {
    // 使用 setTimeout 确保DOM更新完成后再滚动
    setTimeout(scrollToBottom, 100)
  }, [messages, scrollToBottom])

  // 检测JCMD创建的Agent
  useEffect(() => {
    const checkCreatedAgent = () => {
      const createdAgentId = localStorage.getItem('created_agent_id')
      if (createdAgentId && !agentId) {
        console.log('检测到JCMD创建的Agent:', createdAgentId)
        setAgentId(createdAgentId)
        // 清除localStorage中的标记，避免重复显示
        localStorage.removeItem('created_agent_id')
      }
    }

    // 立即检查一次
    checkCreatedAgent()

    // 设置定时器定期检查（每1秒检查一次）
    const interval = setInterval(checkCreatedAgent, 1000)

    return () => clearInterval(interval)
  }, [agentId])

  const handleSendMessage = async (content: string) => {
    if (!content.trim() || !currentSession) return
    
    // 如果agentId不为空，表示该会话已经创建了对应的agent，禁止发送消息
    if (agentId) {
      console.log('该会话已创建Agent，禁止发送消息')
      return
    }

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: content.trim(),
      timestamp: new Date(),
      sessionId: currentSession.sessionId
    }

    setMessages(prev => [...prev, userMessage])
    setInputValue("")
    setTextareaHeight(80) // 重置输入框高度
    console.log("inputRef.current",inputRef.current)
    // 同步更新DOM元素的高度
    if (inputRef.current ) {
      inputRef.current.style.height = '80px'
    }
    
    setIsLoading(true)

    try {
      // 发送消息到服务器
      console.log('调用send API，sessionId:', currentSession.sessionId)
      console.log('选择的模型:', selectedModel)
      console.log('模型ID:', selectedModel?.id)
      
      // 先创建一个空的AI消息用于流式更新
      const aiMessageId = `ai-${Date.now()}`
      const aiMessage: Message = {
        id: aiMessageId,
        type: 'ai',
        content: '',
        timestamp: new Date(),
        sessionId: currentSession.sessionId,
        metadata: {
          modelId: selectedModel?.id
        }
      }
      
      setMessages(prev => [...prev, aiMessage])
      
      // 调用流式API
      const response = await agentService.sendMessage(
        currentSession.sessionId, 
        {
          content: content.trim(),
          modelId: selectedModel?.id || undefined
        },
        (chunk) => {
          // 实时更新AI消息内容
          if (chunk.type === 'chunk') {
            setMessages(prev => prev.map(msg => 
              msg.id === aiMessageId 
                ? { ...msg, content: msg.content + chunk.content }
                : msg
            ))
          }
        }
      )
      
      console.log('send API 完成，最终响应:', response)
      
      // 更新最终的消息内容
      setMessages(prev => prev.map(msg => 
        msg.id === aiMessageId 
          ? { ...msg, content: response.content, id: response.id }
          : msg
      ))
      
      // 如果是第一条消息，更新会话标题
      if (messages.length === 0 && currentSession) {
        try {
          const updatedSession = await agentService.updateSessionTitle(
            currentSession.sessionId, 
            content.trim().length > 20 ? content.trim().substring(0, 20) + '...' : content.trim()
          )
          setCurrentSession(updatedSession)
        } catch (error) {
          console.error('更新会话标题失败:', error)
        }
      }
      
      // 开始Agent处理流程
      startAgentProcess(content.trim())
    } catch (error) {
      console.error('发送消息失败:', error)
      
      // 如果API调用失败，显示错误消息
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'ai',
        content: '抱歉，发送消息时出现错误，请稍后重试。',
        timestamp: new Date(),
        sessionId: currentSession.sessionId
      }
      
      setMessages(prev => [...prev, errorMessage])
    } finally {
      setIsLoading(false)
    }
  }

  const startAgentProcess = async (userInput: string) => {
    // 更新第一步：意图识别
    setAgentSteps(prev => prev.map(step => 
      step.id === '1' ? { ...step, status: 'processing' } : step
    ))

    // 模拟意图识别
    await new Promise(resolve => setTimeout(resolve, 1500))
    setAgentSteps(prev => prev.map(step => 
      step.id === '1' ? { 
        ...step, 
        status: 'completed',
        content: `用户意图：${userInput}\n\n分析结果：\n- 主要需求：${userInput}\n- 任务类型：信息查询/任务执行\n- 优先级：高`
      } : step
    ))

    // 更新第二步：数据整合
    setAgentSteps(prev => prev.map(step => 
      step.id === '2' ? { ...step, status: 'processing' } : step
    ))

    await new Promise(resolve => setTimeout(resolve, 1200))
    setAgentSteps(prev => prev.map(step => 
      step.id === '2' ? { 
        ...step, 
        status: 'completed',
        content: `数据收集完成：\n\n- 相关数据源：已连接\n- 数据验证：通过\n- 数据预处理：完成\n- 可用数据量：充足`
      } : step
    ))

    // 更新第三步：执行工具
    setAgentSteps(prev => prev.map(step => 
      step.id === '3' ? { ...step, status: 'processing' } : step
    ))

    await new Promise(resolve => setTimeout(resolve, 1000))
    setAgentSteps(prev => prev.map(step => 
      step.id === '3' ? { 
        ...step, 
        status: 'completed',
        content: `工具执行计划：\n\n- 选择工具：智能助手\n- 执行策略：分步骤处理\n- 预期结果：满足用户需求\n- 执行状态：准备就绪`
      } : step
    ))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    // 如果agentId不为空，表示该会话已经创建了对应的agent，禁止发送消息
    if (agentId) {
      console.log('该会话已创建Agent，禁止发送消息')
      return
    }
    
    handleSendMessage(inputValue)
  }

  // 重试连接会话
  const retrySessionConnection = async () => {
    if (!isAuthenticated) return
    
    setSessionError(null)
    setIsInitializing(true)
    
    try {
      const session = await agentService.getCurrentSession()
      setCurrentSession(session)
      
      const history = await agentService.getSessionHistory(session.sessionId)
      const convertedMessages: Message[] = (history.messages || [])
        .filter(msg => msg.content !== null)
        .map((msg) => ({
          id: msg.messageId || `history-${Date.now()}-${Math.random()}`,
          type: msg.role === 'user' ? 'user' : 'ai',
          content: msg.content!,
          timestamp: new Date(parseInt(msg.gmtCreated)),
          sessionId: session.sessionId,
          metadata: {
            modelId: selectedModel?.id
          }
        }))
      
      setMessages(convertedMessages)
    } catch (error) {
      console.error('重试连接失败:', error)
      setSessionError('连接失败，请检查网络连接后重试')
    } finally {
      setIsInitializing(false)
    }
  }

  // 防抖调整textarea高度
  const adjustTextareaHeight = useCallback((element: HTMLTextAreaElement) => {
    // 清除之前的定时器
    if (heightAdjustTimeoutRef.current) {
      clearTimeout(heightAdjustTimeoutRef.current)
    }
    
    // 设置新的定时器，延迟调整高度
    heightAdjustTimeoutRef.current = setTimeout(() => {
      // 安全检查：确保element仍然存在且有效
      if (!element || !element.style) {
        console.warn('Textarea element is no longer valid')
        return
      }
      
      // 重置高度以获取正确的scrollHeight
      element.style.height = 'auto'
      
      // 计算新高度，最小80px，最大为页面高度的50%
      const maxHeight = window.innerHeight * 0.5
      const newHeight = Math.min(Math.max(element.scrollHeight, 80), maxHeight)
      
      // 只有当高度真正改变时才更新状态
      if (Math.abs(newHeight - textareaHeight) > 5) {
        setTextareaHeight(newHeight)
        element.style.height = `${newHeight}px`
      } else {
        element.style.height = `${newHeight}px`
      }
    }, 100) // 100ms 防抖延迟
  }, [textareaHeight])

  // 防抖处理输入变化
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value)
    adjustTextareaHeight(e.target)
  }, [adjustTextareaHeight])

  // 清理定时器
  useEffect(() => {
    return () => {
      if (heightAdjustTimeoutRef.current) {
        clearTimeout(heightAdjustTimeoutRef.current)
      }
    }
  }, [])


  // 如果正在检查认证状态或初始化会话，显示加载状态
  if (isCheckingAuth || isInitializing) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">
            {isCheckingAuth ? "检查登录状态..." : "初始化会话..."}
          </p>
        </div>
      </div>
    )
  }

  // 如果未认证，不显示内容（会被重定向到登录页）
  if (!isAuthenticated) {
    return null
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="pt-20 pb-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">{t('agent.title')}</h1>
            <p className="text-gray-600 mt-2">{t('agent.subtitle')}</p>
          </div>

          {/* 直接展示对话区域，不再放在Tabs内 */}
          <div className="w-full">
            <Card className="flex flex-col" style={{ height: 'calc(100vh - 160px - 256px)', minHeight: '500px' }}>
              <CardHeader className="flex-shrink-0 pb-4">
                {/* CardTitle 整块删除，不再显示标题 */}
                <CardDescription>
                  <div className="flex items-center">
                    <Bot className="mr-2 h-5 w-5 text-blue-500" />
                    {(() => {
                      const lang = typeof window !== 'undefined' ? (localStorage.getItem('lang') || navigator.language) : 'zh-CN'
                      if (lang.startsWith('en')) {
                        return 'Just talk with us about your idea'
                      } else {
                        return '说出你的创意吧'
                      }
                    })()}
                  </div>
                </CardDescription>
              </CardHeader>
              <div className="flex-1 flex flex-col min-h-0">
                <div className="flex-1 overflow-hidden px-6 pb-4">
                  <ScrollArea ref={scrollAreaRef} className="h-full">
                    <div className="space-y-4 py-4">
                      {sessionError && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <div className="text-red-600 text-sm">
                                {sessionError}
                              </div>
                            </div>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={retrySessionConnection}
                              className="text-red-600 border-red-200 hover:bg-red-50"
                            >
                              重试
                            </Button>
                          </div>
                        </div>
                      )}
                      {messages.map((message) => (
                        <div
                          key={message.id}
                          className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                        >
                          <div
                            className={`max-w-[80%] rounded-lg p-3 ${
                              message.type === 'user'
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-100 text-gray-900'
                            }`}
                          >
                            <div className="flex items-start space-x-2">
                              {message.type === 'ai' && (
                                <Bot className="h-4 w-4 mt-0.5 flex-shrink-0" />
                              )}
                              <div className="flex-1">
                                <div className="text-sm">
                                  <MarkdownRenderer content={message.content} agentId={agentId || undefined} />
                                </div>
                                <p className="text-xs opacity-70 mt-1">
                                  {formatMessageTime(message.timestamp)}
                                </p>
                              </div>
                              {message.type === 'user' && (
                                <User className="h-4 w-4 mt-0.5 flex-shrink-0" />
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      {isLoading && (
                        <div className="flex justify-start">
                          <div className="bg-gray-100 text-gray-900 rounded-lg p-3">
                            <div className="flex items-center space-x-2">
                              <Bot className="h-4 w-4" />
                              <div className="flex space-x-1">
                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      {/* 当agentId不为空时，显示Agent已创建的通知 */}
                      {agentId && (
                        <div className="flex justify-start">
                          <div className="bg-green-50 border border-green-200 rounded-lg p-4 max-w-[80%]">
                            <div className="flex items-start space-x-3">
                              <div className="flex-shrink-0">
                                <Bot className="h-5 w-5 text-green-600" />
                              </div>
                              <div className="flex-1">
                                <div className="text-sm font-medium text-green-800 mb-2">
                                  🎉 Agent 已创建成功！
                                </div>
                                <div className="text-sm text-green-700 mb-3">
                                  基于您的需求，我们已经成功创建了一个智能Agent。现在您可以查看和管理这个Agent的详细配置。
                                </div>
                                <Button
                                  onClick={() => navigate(`/agent/${agentId}`)}
                                  className="bg-green-600 hover:bg-green-700 text-white text-sm"
                                  size="sm"
                                >
                                  <ArrowRight className="h-4 w-4 mr-1" />
                                  查看Agent详情
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      <div ref={messagesEndRef} />
                    </div>
                  </ScrollArea>
                </div>
                {!agentId && (
                  // <form onSubmit={handleSubmit} className="px-6 py-4 flex-shrink-0 bg-white border-t sticky bottom-0 z-10">
                    <div className="relative">
                      <Textarea
                        ref={inputRef}
                        value={inputValue}
                        onChange={handleInputChange}
                        placeholder={t('agent.inputPlaceholder')}
                        className="w-full resize-none pr-16 overflow-hidden"
                        style={{ height: `${textareaHeight}px`, minHeight: '80px' }}
                        onKeyUp={(e) => {
                          if(e.key === 'Enter' && !e.shiftKey){
                            handleSubmit(e)
                          }
                          // console.log('onKeyUp事件:', e.key, 'code:', e.code)
                        }}
                        disabled={isLoading}
                      />
                      <Button 
                        type="submit" 
                        disabled={isLoading || !inputValue.trim()}
                        className="absolute right-2 bottom-2 h-8 w-8 p-0"
                        style={{ bottom: '8px' }}
                        onClick={handleSubmit}
                      >
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>
                  // </form>
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  )
} 